/**
 * Resource Types
 *
 * TypeScript interfaces for resource management
 */

export type ResourceType =
  | "VirtualMachine"
  | "Storage"
  | "Network"
  | "Database"
  | "LoadBalancer"
  | "ObjectStorage"
  | "Domain";

export type ResourceStatus =
  | "Pending"
  | "Provisioning"
  | "Running"
  | "Stopped"
  | "Suspended"
  | "Error"
  | "Terminating"
  | "Terminated"
  /**
   * Mirrors backend `ResourceStatus.Decommissioning`: a deleted VM kept until it is purged, or a VM
   * being reinstalled in place. A row visible through the normal list/detail endpoints with this
   * status always means a reinstall is in flight — a deleted VM is only reachable through
   * `GET /resources/deleted`, never through the ordinary reads.
   */
  | "Decommissioning";

export type ProvisioningStage =
  | "Queued"
  | "Requesting"
  | "Cloning"
  | "Booting"
  | "Completed"
  | "Failed";

export interface ProvisioningStatus {
  resourceId: string;
  resourceStatus: ResourceStatus;
  stage: ProvisioningStage;
  startedAt?: string | null;
  updatedAt?: string | null;
}

// Legacy status mapping for backward compatibility
export type LegacyResourceStatus =
  | "provisioning"
  | "active"
  | "stopped"
  | "failed"
  | "deleting"
  | "suspended";

export interface Resource {
  id: string;
  /**
   * Provider-side identifier (e.g. WiService VM UUID), for quoting to support. Mirrors backend
   * ResourceListDto.ProviderResourceId.
   *
   * Written as soon as the provider accepts the create call, so it is already set while the VM is
   * `Provisioning` and nothing exists on the hypervisor yet. It is therefore never a readiness
   * signal on its own — pair it with the status via `canQueryProvider`.
   */
  providerResourceId?: string | null;
  /** Product-line slug recorded on the VM ("cheap", "performance"). Detail endpoint only; null for a non-VM resource and for older VMs. Mirrors backend ResourceDto.ProductLine. */
  productLine?: string | null;
  /** CPU chip type the VM was ordered on ("Intel", "AMD"). Detail endpoint only; null on the same terms as `productLine`. Mirrors backend ResourceDto.ChipType. */
  chipType?: string | null;
  /** Identifier of the order that provisioned this resource. Absent for resources created outside the order flow (e.g. trials). Mirrors backend ResourceDto.OrderId. */
  orderId?: string;
  tenantId: string;
  name: string;
  type: string;
  status: ResourceStatus;
  osImage: {
    id: string;
    /** Template name, which already carries the version (e.g. "Windows-10-Pro", "Ubuntu-24.04"). */
    name: string;
    /** OS version, only when the backend reports one separately from the name. Empty otherwise. */
    version: string;
    iconUrl: string;
  };
  specs: ResourceSpecs;
  ipAddress?: string;
  region: string;
  hourlyCost: {
    amount: number;
    currency: string;
  };
  monthlyCost: {
    amount: number;
    currency: string;
  };
  /**
   * What the customer actually pays per month, after any commitment discount.
   *
   * `monthlyCost` above is the LIST rate — the undiscounted figure the backend re-prices and
   * renews from. Show THIS one wherever the label says "your monthly cost": a 12-month
   * commitment at 15% off pays this, not that. Equal to `monthlyCost` when no discount applies,
   * or when there is no billing cycle to derive one from.
   *
   * Optional so a response from an older backend still parses; fall back to `monthlyCost`.
   */
  effectiveMonthlyCost?: {
    amount: number;
    currency: string;
  };
  provisionedAt?: string;
  /** Paid-through (active billing-cycle end) timestamp, ISO 8601. Mirrors backend ResourceDto/ResourceListDto ExpiresAt. */
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  lastAction?: string;
  cpuUsagePercent?: number;
  memUsagePercent?: number;
  /** Provisioning diagnostics; only present while/after the resource went through provisioning. */
  provisioning?: ResourceProvisioningInfo;
  /**
   * When the running reinstall was picked up by the job, ISO 8601 UTC. Detail endpoint only, and
   * only while status is `Decommissioning`; null in the first seconds after the request and at
   * every other time. Mirrors backend ResourceDto.ReinstallStartedAt.
   */
  reinstallStartedAt?: string | null;
  /** True while the rebuild in flight is the restore of a deleted VM, not an in-place reinstall. */
  isRestoring?: boolean;
  /**
   * When the infrastructure last refused to delete this VM for good and it was put back, ISO 8601
   * UTC. Detail endpoint only; null when no deletion ever failed. Mirrors backend
   * ResourceDto.DeletionFailedAt.
   */
  deletionFailedAt?: string | null;
}

/**
 * Customer-facing failure classification. Mirrors backend `ProvisioningFailureCodes`. It alone
 * decides the wording about the customer's money; the raw failure line travels beside it as
 * `ResourceProvisioningInfo.errorMessage` and is only quoted.
 */
export const PROVISIONING_FAILURE_CODES = [
  "ProvisioningFailed",
  "ProvisioningTimeout",
] as const;
export type ProvisioningFailureCode =
  (typeof PROVISIONING_FAILURE_CODES)[number];

/**
 * Mirrors backend ResourceProvisioningInfoDto — the customer-facing slice of the provisioning
 * pipeline: enough to show progress and explain the outcome, and nothing more.
 */
export interface ResourceProvisioningInfo {
  /** When the provisioning job started; drives the elapsed-time budget in the UI. */
  startedAt?: string;
  /** When provisioning failed. */
  failedAt?: string;
  /** Null while provisioning is in flight or succeeded. */
  failureCode?: ProvisioningFailureCode;
  /**
   * True when provisioning exhausted its polling window instead of failing outright. That
   * branch deliberately does NOT refund — an operator must verify the VM first, so the UI must
   * never promise an automatic refund when this is set.
   */
  requiresManualVerification?: boolean;
  /**
   * The raw failure line the job recorded, the same text the admin console shows. Rendered as-is
   * and NOT translated — it is the job's and the provider's own wording (user decision
   * 2026-10-02, reversing the 2026-09-30 withdrawal). Absent when no failure is recorded.
   */
  errorMessage?: string | null;
}

/**
 * One entry of `POST /resources/retry-provisioning/check` (client) and the admin equivalent.
 * Whether "Retry provisioning" may be offered right now for one resource — the status is Error,
 * the failure was a provisioning failure, the order/billing cycle check out, and the VM is within
 * its 24h window. An id the caller may not see comes back `allowed: false` with `RESOURCE_NOT_FOUND`.
 */
export interface ProvisioningRetryCheck {
  allowed: boolean;
  /** Machine-readable refusal code (e.g. RETRY_WINDOW_EXPIRED), or null when allowed. */
  reasonCode: string | null;
  /** Human-readable refusal reason, or null when allowed. Not shown to the customer today. */
  reason: string | null;
  /**
   * No provider id is recorded, so the last create request may have built a machine WiCloud never
   * heard back about. Computed server-side so both consoles agree; a customer can only retry a VM
   * that has a provider id.
   */
  machineMayExist: boolean;
}

/**
 * What the customer fills in before a VM is destroyed. Both fields are re-checked by the backend:
 * DeleteResourceCommandValidator bounds the reason's length (5-500 after trimming) and
 * DeleteResourceCommandHandler requires both for a VM, with confirmationName having to equal the
 * VM name exactly (case-sensitive, surrounding spaces ignored).
 */
export interface DeleteResourcePayload {
  reason: string;
  confirmationName: string;
}

/**
 * A resource's current lifecycle state, as `GET /resources/statuses` reports it — the cheap read a
 * page uses to watch a few resources change state. Mirrors backend `ResourceStatusDto`. Deleted
 * resources are included; all dates are UTC ISO strings.
 */
export interface ResourceStatusDto {
  id: string;
  name: string;
  type: ResourceType;
  status: ResourceStatus;
  /** When the resource was deleted; null while it is live. */
  deletedAt: string | null;
  /** Until when a deleted VM is kept before it is purged; null while it is live or has no machine. */
  restorableUntil: string | null;
  /** When the infrastructure last refused to delete it for good; null when that never happened. */
  deletionFailedAt: string | null;
  updatedAt: string;
}

/**
 * One of the caller's VMs deleted within the last `RESTORE_WINDOW_HOURS` hours — the
 * "Deleted" tab. Mirrors backend `DeletedResourceDto`. All dates are UTC ISO strings.
 */
export interface DeletedResourceDto {
  id: string;
  name: string;
  deletedAt: string;
  restorableUntil: string;
  templateName: string | null;
  ipAddress: string | null;
  /** Configuration the VM is rebuilt with (a reinstall only changes the OS); null when not recorded. */
  cores: number | null;
  memoryMb: number | null;
  diskGb: number | null;
}

/** How long a deleted VM with a real machine is kept before it is purged, in hours. Mirrors `Resource.RestoreWindow`. */
export const RESTORE_WINDOW_HOURS = 1;

/** Shortest deletion reason the backend accepts - mirrors DeleteResourceCommandValidator. */
export const DELETE_REASON_MIN_LENGTH = 5;

/** Longest deletion reason the backend accepts - mirrors DeleteResourceCommandValidator. */
export const DELETE_REASON_MAX_LENGTH = 500;

export interface ResourceConsole {
  resourceId: string;
  consoleUrl: string;
  linkExpiresInSeconds: number;
  userExpiresInMinutes: number;
  generatedAt: string;
}

export interface ResourceSpecs {
  cores?: number; // CPU cores count (renamed from "cpu" for semantic correctness)
  memory?: number; // In MB (backend expects MB, not GB)
  disk?: number; // In GB
  bandwidth?: number; // For pricing calculation only, not sent to backend
  storageTier?: string; // Disk storage tier (e.g., "SSD", "HDD", "NVME")
  bandwidthTier?: string; // Network bandwidth tier (e.g., "Standard", "High")
  backupCount?: number;
  snapshotCount?: number;
  additionalIps?: number;
  maxSnapshots?: number; // Per-VM snapshot limit from order provisioning
  templateName?: string; // Template the VM was cloned from (e.g. "Windows-Server-2025"); decides SSH vs RDP
}

/**
 * Frontend-facing resource metrics format
 * Used by ResourceMetricsTab component for displaying charts
 */
export interface ResourceMetrics {
  cores: MetricDataPoint[];
  memory: MetricDataPoint[];
  network: {
    inbound: MetricDataPoint[];
    outbound: MetricDataPoint[];
  };
  disk: {
    read: MetricDataPoint[];
    write: MetricDataPoint[];
  };
}

export interface MetricDataPoint {
  timestamp: string; // ISO 8601 format
  value: number;
}

/**
 * Backend response format for metrics endpoint
 * Matches ResourceMetricsTimeSeriesDto from backend
 */
export interface BackendResourceMetricsResponse {
  resourceId: string;
  timeframe: string;
  cpuUsage: MetricDataPoint[]; // percentage 0-100
  memoryUsageMB: MetricDataPoint[]; // megabytes
  memoryUsagePercent: MetricDataPoint[]; // percentage 0-100
  networkInMbps: MetricDataPoint[]; // Mbps (megabits per second)
  networkOutMbps: MetricDataPoint[]; // Mbps (megabits per second)
  diskReadBytesPerSec: MetricDataPoint[]; // bytes/sec
  diskWriteBytesPerSec: MetricDataPoint[]; // bytes/sec
  diskUsagePercent: MetricDataPoint[]; // may be empty
  totalCores: number;
  totalMemoryMB: number;
  totalDiskGB: number;
  startTime: string;
  endTime: string;
}

/**
 * Valid time range options for metrics
 */
export type MetricsTimeRange = "1h" | "24h" | "7d" | "30d" | "1y";

export interface Snapshot {
  id: string; // WiService uses "name" as ID
  name: string;
  description?: string;
  resourceId: string;
  size: number; // in bytes (0 if not provided by WiService)
  createdAt: string;
  status: "creating" | "available" | "failed" | "deleting" | "restoring";
  executionId?: number; // N8N execution ID for tracking
  snaptime?: number; // Unix epoch timestamp
  parent?: string; // Parent snapshot name for tree
  vmstate?: number; // 0 = no VM state, 1 = includes RAM
}

/**
 * A firewall rule. A blank optional field (undefined or "") means "all" - any address, port or
 * protocol, no macro; see `isAll` in ResourceFirewallTab. The backend maps it to the webhook.
 */
export interface FirewallRule {
  pos?: number;
  type: "in" | "out";
  action: "ACCEPT" | "DROP" | "REJECT";
  enable: number;
  source?: string;
  sport?: string;
  dest?: string;
  dport?: string;
  iface?: string;
  proto?: string;
  log?: string;
  macro?: string;
  comment?: string;
  digest?: string;
}

export interface CreateFirewallRuleRequest {
  type: "in" | "out";
  action: "ACCEPT" | "DROP" | "REJECT";
  enable: number;
  source?: string;
  sport?: string;
  dest?: string;
  dport?: string;
  iface?: string;
  proto?: string;
  log?: string;
  macro?: string;
  comment?: string;
}

export interface FirewallOptions {
  enable: number;
  logLevelIn?: string;
  logLevelOut?: string;
  policyIn?: string;
  policyOut?: string;
  digest?: string;
}

export interface FirewallLog {
  n: number; // Line number
  t: string; // Log text
}

export interface FirewallMacro {
  macro: string;
  desc: string;
}

export type BillingCycleMonths = number;

export interface BillingCycleOption {
  months: BillingCycleMonths;
  discountPercent: number;
  totalPrice: number;
  /** Total price divided by months (same currency as totalPrice). */
  monthlyEquivalent: number;
}

/** Default VAT rate (percentage) when the backend does not provide one. */
export const DEFAULT_VAT_RATE = 10;

export interface PricingCalculation {
  cpuCost: number;
  memoryCost: number;
  diskCost: number;
  totalMonthly: number;
  totalHourly: number;
  currency: string;
  vatRate?: number;
  billingCycleOptions?: BillingCycleOption[];
  componentBreakdown?: Record<string, number>;
}

export interface ResourceFilters {
  status?: ResourceStatus | null;
  osImage?: string | null;
  search?: string;
}

export interface ResourceListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: ResourceStatus;
  type?: ResourceType;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface ResourceListResponse {
  resources: Resource[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * GET /resources/{id}/reinstall/quota — how many reinstalls a VM has left. `limit` and `remaining`
 * are null when there is no limit: the operator has the limits switched off, or set this plan's to 0. `windowEndsAt` (UTC) is when a paid VM's
 * count starts again; null for a trial, whose window is the whole trial, and when no limit applies.
 */
export interface ReinstallQuota {
  vmPlan: "Trial" | "Paid";
  limit: number | null;
  used: number;
  remaining: number | null;
  windowEndsAt: string | null;
}

/** GET /resources/check-name — whether a resource name is free in the tenant */
export interface ResourceNameAvailability {
  name: string;
  available: boolean;
  /** When unavailable, the specific name (the VM name or a derived sibling) that is taken. */
  conflictingName?: string | null;
  /** Short machine-readable reason: "exact" | "sibling" | "empty" | null. */
  reason?: string | null;
}

/** GET /resources/vms/summary — tenant VM aggregates + status distribution */
export interface VMStatusSummaryDto {
  total: number;
  statusCounts: Record<string, number>;
  allocatedCores: number;
  allocatedMemoryMb: number;
  totalMonthlyCost: number;
  totalHourlyCost: number;
  createdLast24Hours: number;
  firstErrorResourceName?: string | null;
}

// ==========================================
// Standalone Disk and Network Resource Types
// ==========================================

export interface AttachedDisk {
  resourceId: string;
  resourceName: string;
  sizeGB: number;
  deviceId: string;
  iops: number;
  attachedAt: string;
}

export interface AttachedNetwork {
  resourceId: string;
  resourceName: string;
  bandwidth: string;
  deviceId: string;
  vlanId?: number;
  ipAddress?: string;
  macAddress?: string;
  switch?: string;
  firewallEnabled?: boolean;
  attachedAt: string;
}

export interface ResourceAttachments {
  disks: AttachedDisk[];
  networks: AttachedNetwork[];
}

export interface AttachDiskRequest {
  deviceId?: string;
  busType?: "scsi" | "sata" | "virtio";
  isReadOnly?: boolean;
}

export interface AttachNetworkRequest {
  deviceId?: string;
  vlanId?: number;
}

export interface AttachResourceResponse {
  attachmentId: string;
  deviceId: string;
  attachedAt: string;
}

// Socket.IO Event Types for Resource Attachments
export interface ResourceAttachedEvent {
  attachmentId: string;
  parentResourceId: string;
  childResourceId: string;
  attachmentType: "disk" | "network";
  deviceId: string;
  timestamp: string;
}

export interface ResourceDetachedEvent {
  attachmentId: string;
  parentResourceId: string;
  childResourceId: string;
  attachmentType: "disk" | "network";
  timestamp: string;
}

export interface ResourceStatusChangedEvent {
  resourceId: string;
  resourceType: "disk" | "network" | "vm";
  status: string;
  previousStatus: string;
  timestamp: string;
}

// ==========================================
// Resource Credentials Types
// ==========================================

/**
 * Result of a VM password reset. The password is returned once, on this response only — every later
 * read goes through the credentials endpoint.
 */
export interface ResetVmPasswordResult {
  resourceId: string;
  username: string;
  newPassword: string;
  /** The running machine keeps the old password until it boots again. */
  requiresReboot: boolean;
}

export interface ResourceCredentials {
  resourceId: string;
  username: string;
  password: string;
  ipAddress?: string;
  sshPort?: number;
  rdpPort?: number;
  connectionType: string; // "SSH" or "RDP"
  retrievedAt?: string;
}

/**
 * What the customer did with the VM password on the credentials tab — mirrors the backend's
 * CredentialAccessKind. Each one is written to the audit trail.
 */
export const CREDENTIAL_ACCESS_KINDS = [
  "PasswordRevealed",
  "PasswordCopiedByButton",
  "PasswordCopiedFromField",
] as const;
export type CredentialAccessKind = (typeof CREDENTIAL_ACCESS_KINDS)[number];

// ==========================================
// Object Storage Types
// ==========================================

export interface ObjectStorageBucket {
  bucketName: string;
  quotaBucketGb: number;
  sizeUsageGb: number;
}

export interface ObjectStorageBucketsResponse {
  resourceId: string;
  minioUser: string;
  minioUrl: string;
  minioConsole: string;
  buckets: ObjectStorageBucket[];
}

export interface ObjectStorageCredentials {
  resourceId: string;
  minioUser: string;
  password: string;
  minioUrl: string;
  minioConsole: string;
  retrievedAt?: string;
}

export interface CreateBucketRequest {
  bucketName: string;
  quotaBucketGb?: number;
}

export interface CreateBucketResponse {
  bucketName: string;
  quotaBucketGb: number;
  minioUrl: string;
  minioConsole: string;
}

export interface UpdateBucketQuotaRequest {
  quotaBucketGb: number;
}

export interface UpdateBucketQuotaResponse {
  bucketName: string;
  quotaBucketGb: number;
}

export interface ResetStoragePasswordResponse {
  newPassword: string;
  minioUrl: string;
  minioConsole: string;
}
